import { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { useGamification } from '@/hooks/useGamification';

interface CreateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateCampaignModal({ isOpen, onClose }: CreateCampaignModalProps) {
  const { saveCampaign } = useGamification();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reward_type: 'coupon' as 'url' | 'text' | 'coupon' | 'ar',
    reward_data: { code: '', url: '', text: '' }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveCampaign.mutate(formData, {
      onSuccess: () => {
        onClose();
        setFormData({
          title: '',
          description: '',
          reward_type: 'coupon',
          reward_data: { code: '', url: '', text: '' }
        });
      }
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Gamification Campaign</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Campaign Title</Label>
            <Input 
              id="title" 
              placeholder="e.g. Summer Scavenger Hunt" 
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea 
              id="description" 
              placeholder="Describe the hunt for your users..." 
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reward Type</Label>
              <Select 
                value={formData.reward_type}
                onValueChange={(v: any) => setFormData({ ...formData, reward_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="coupon">Coupon Code</SelectItem>
                  <SelectItem value="url">Redirect URL</SelectItem>
                  <SelectItem value="text">Secret Message</SelectItem>
                  <SelectItem value="ar">AR Experience</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Reward Detail</Label>
              {formData.reward_type === 'coupon' && (
                <Input 
                  placeholder="COUPON20" 
                  value={formData.reward_data.code}
                  onChange={e => setFormData({ 
                    ...formData, 
                    reward_data: { ...formData.reward_data, code: e.target.value } 
                  })}
                />
              )}
              {formData.reward_type === 'url' && (
                <Input 
                  placeholder="https://..." 
                  value={formData.reward_data.url}
                  onChange={e => setFormData({ 
                    ...formData, 
                    reward_data: { ...formData.reward_data, url: e.target.value } 
                  })}
                />
              )}
              {formData.reward_type === 'text' && (
                <Input 
                  placeholder="The secret is..." 
                  value={formData.reward_data.text}
                  onChange={e => setFormData({ 
                    ...formData, 
                    reward_data: { ...formData.reward_data, text: e.target.value } 
                  })}
                />
              )}
              {formData.reward_type === 'ar' && (
                <p className="text-xs text-muted-foreground pt-2 italic">Uses standard AR model from steps</p>
              )}
            </div>
          </div>

          <DialogFooter className="pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saveCampaign.isPending}>
              {saveCampaign.isPending ? 'Creating...' : 'Create Campaign'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
